/**
 * aurora — dancing VERTICAL light curtains high in the sky (polar lights). A row
 * of tall `ray_ribbon` shafts hanging from the upper sky — the SAME texture
 * `godRays` renders vertically, so they actually read (an earlier wide-stretched
 * version went invisible). Each shaft is tinted from the palette and, on its own
 * sine, sways horizontally + leans + alpha-shimmers; the whole curtain drifts
 * slowly. Sits in the sky band (Z_LAYERS.CLOUDS); a constant fixture (mooded:false).
 *
 * Same builder shape as the kit: narrow by kind, place from the seed, mint sprites
 * behind IRenderer (null-safe), drive them with ONE shared ctx.addTick, self-
 * register. REDUCE-MOTION: the dance is the builder's own tick → when reduced we
 * place the shafts STATIC and never tick. The pref is read ONCE at build.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const TWO_PI = Math.PI * 2;

const buildAurora: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'aurora') return null;
  const { region } = ctx;
  const spanX = region.x1 - region.x0;
  const spanY = region.y1 - region.y0;
  const reduceMotion = getMotionPrefs().reduceMotion;
  const colors = layer.colors.length ? layer.colors : [0x66ffcc];

  const sprites: ISprite[] = [];
  // Per-shaft dance state: sprite, its centre-x home, vertical centre, drift, period/phase.
  const shafts: { s: ISprite; homeX: number; cy: number; vx: number; periodMs: number; phase: number }[] = [];

  for (let i = 0; i < layer.bands; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 5100 + i * 23.1;
    const width = lerp(layer.widthMin, layer.widthMax, seededRand(seed));
    const height = lerp(layer.heightMin, layer.heightMax, seededRand(seed + 1));
    // Spread shafts across the span; anchor the TOP high in the sky, so the shaft
    // (centred sprite) hangs down from there → centre-y = topY + height/2.
    const homeX = lerp(region.x0 + spanX * 0.05, region.x1 - spanX * 0.05, (i + 0.5) / layer.bands + (seededRand(seed + 2) - 0.5) * 0.08);
    const topY = region.y0 + spanY * lerp(layer.bandYMin, layer.bandYMax, seededRand(seed + 3));
    const cy = topY + height / 2;
    const color = colors[i % colors.length];
    const alpha = lerp(layer.alphaMin, layer.alphaMax, 0.5); // start mid; the tick drives it
    const s = createGlowSprite({ x: homeX, y: cy, size: width, color, alpha, textureKey: 'ray_ribbon', blendMode: 'add', parent: ctx.layerContainer });
    if (!s) break; // textures not ready — quieter sky, never a crash
    s.setDisplaySize(width, height); // tall vertical shaft (godRays convention)
    sprites.push(s);
    if (!reduceMotion) {
      const dir = i % 2 === 0 ? 1 : -1;
      shafts.push({ s, homeX, cy, vx: layer.driftPxPerSec * dir, periodMs: lerp(layer.periodMinMs, layer.periodMaxMs, seededRand(seed + 4)), phase: seededRand(seed + 5) });
    }
  }

  if (!sprites.length) return null;
  if (reduceMotion || !shafts.length) return makeContentHandle({ sprites });

  // ONE shared tick sways + leans + shimmers + drifts every shaft. addTick returns
  // its own unsubscribe — destroy() MUST call it (else the closure leaks).
  const margin = layer.swayPx + 40;
  let elapsedMs = 0;
  const unsub = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    const v = ctx.getCameraView();
    const left = (v ? v.centerX - v.width / 2 : region.x0) - margin;
    const span = (v ? v.width : region.x1 - region.x0) + margin * 2;
    const dt = dtMs / 1000;
    for (const sh of shafts) {
      const ph = (elapsedMs / sh.periodMs + sh.phase) % 1;
      const sway = Math.sin(ph * TWO_PI) * layer.swayPx;
      const a = lerp(layer.alphaMin, layer.alphaMax, 0.5 - 0.5 * Math.cos(ph * TWO_PI));
      sh.homeX += sh.vx * dt;
      // Wrap the whole curtain across the live camera span.
      if (sh.homeX > left + span) sh.homeX -= span;
      else if (sh.homeX < left) sh.homeX += span;
      sh.s.setPosition(sh.homeX + sway, sh.cy);
      sh.s.setAngle(Math.sin(ph * TWO_PI) * 10); // lean ±10° — the curtain ripples
      sh.s.setAlpha(a);
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'aurora', build: buildAurora, depth: Z_LAYERS.CLOUDS, mooded: false });
